import type { AdhesionProposalItem } from '../types/vida/table/adhesionProposalTableType';
import { mapProponentStatus } from './proponentStatusUtils';
import { formatSubestipulanteCodNome } from './subestipulanteUtil';

export const formatAdhesionProposalSubestipulante = (
  row: Pick<AdhesionProposalItem, 'subCompanyCod' | 'subCompanyName'>,
): string => formatSubestipulanteCodNome(row.subCompanyCod, row.subCompanyName);

export const ADHESION_PROPOSAL_SORT_OPTIONS = [
  { label: 'ID (Menor)', value: 'id' },
  { label: 'ID (Maior)', value: 'id-desc' },
  { label: 'Status (A-Z)', value: 'status' },
  { label: 'Status (Z-A)', value: 'status-desc' },
  { label: 'Data do Status (A-Z)', value: 'statusAt' },
  { label: 'Data do Status (Z-A)', value: 'statusAt-desc' },
  { label: 'CPF (A-Z)', value: 'cpf' },
  { label: 'CPF (Z-A)', value: 'cpf-desc' },
  { label: 'Proponente (A-Z)', value: 'proponente' },
  { label: 'Proponente (Z-A)', value: 'proponente-desc' },
  { label: 'Prazo Decurso (Menor)', value: 'prazoDec' },
  { label: 'Prazo Decurso (Maior)', value: 'prazoDec-desc' },
  { label: 'Sub-Estipulante (A-Z)', value: 'subEstipulante' },
  { label: 'Sub-Estipulante (Z-A)', value: 'subEstipulante-desc' },
];

export type AdhesionProposalPanelFilters = {
  id: string;
  status: string;
  cpf: string;
  proponente: string;
  subEstipulante: string;
};

export const EMPTY_ADHESION_PROPOSAL_PANEL_FILTERS: AdhesionProposalPanelFilters = {
  id: '',
  status: '',
  cpf: '',
  proponente: '',
  subEstipulante: '',
};

const matchesPanelTextFilter = (fieldValue: string, filterValue: string): boolean => {
  const term = filterValue.trim();
  if (term === '') {
    return true;
  }
  return fieldValue.toLowerCase().includes(term.toLowerCase());
};

export const filterAdhesionProposalTableRows = (
  rows: AdhesionProposalItem[],
  filters: AdhesionProposalPanelFilters | null,
): AdhesionProposalItem[] => {
  if (!filters) {
    return rows;
  }

  const hasAnyFilter = Object.values(filters).some((value) => value.trim() !== '');
  if (!hasAnyFilter) {
    return rows;
  }

  return rows.filter((row) =>
    matchesPanelTextFilter(String(row.id), filters.id)
    && matchesPanelTextFilter(mapProponentStatus(row.status), filters.status)
    && matchesPanelTextFilter(row.proposerCpf, filters.cpf)
    && matchesPanelTextFilter(row.proposerName, filters.proponente)
    && matchesPanelTextFilter(formatAdhesionProposalSubestipulante(row), filters.subEstipulante));
};

export const filterAdhesionProposalTableBySearch = (
  rows: AdhesionProposalItem[],
  searchTerm: string,
  formatCpf: (cpf: string) => string,
  formatDate: (date: string) => string,
): AdhesionProposalItem[] => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) {
    return rows;
  }

  return rows.filter((row) => {
    const statusLabel = mapProponentStatus(row.status).toLowerCase();
    const cpfFormatted = formatCpf(row.proposerCpf).toLowerCase();
    const cpfRaw = row.proposerCpf.replace(/\D/g, '');
    const termDigits = term.replace(/\D/g, '');

    return (
      String(row.id).includes(term)
      || statusLabel.includes(term)
      || row.proposerName.toLowerCase().includes(term)
      || cpfFormatted.includes(term)
      || (termDigits !== '' && cpfRaw.includes(termDigits))
      || formatAdhesionProposalSubestipulante(row).toLowerCase().includes(term)
      || formatDate(row.statusAt).toLowerCase().includes(term)
      || (row.prazoDec !== null && String(row.prazoDec).includes(term))
    );
  });
};

export const sortAdhesionProposalTableRows = (
  rows: AdhesionProposalItem[],
  sortBy: string,
): AdhesionProposalItem[] => {
  if (!sortBy) {
    return rows;
  }

  return [...rows].sort((a, b) => {
    switch (sortBy) {
      case 'id': return a.id - b.id;
      case 'id-desc': return b.id - a.id;
      case 'status': return mapProponentStatus(a.status).localeCompare(mapProponentStatus(b.status));
      case 'status-desc': return mapProponentStatus(b.status).localeCompare(mapProponentStatus(a.status));
      case 'statusAt': return a.statusAt.localeCompare(b.statusAt);
      case 'statusAt-desc': return b.statusAt.localeCompare(a.statusAt);
      case 'cpf': return a.proposerCpf.localeCompare(b.proposerCpf);
      case 'cpf-desc': return b.proposerCpf.localeCompare(a.proposerCpf);
      case 'proponente': return a.proposerName.localeCompare(b.proposerName);
      case 'proponente-desc': return b.proposerName.localeCompare(a.proposerName);
      case 'prazoDec': return (a.prazoDec ?? -1) - (b.prazoDec ?? -1);
      case 'prazoDec-desc': return (b.prazoDec ?? -1) - (a.prazoDec ?? -1);
      case 'subEstipulante': return formatAdhesionProposalSubestipulante(a).localeCompare(formatAdhesionProposalSubestipulante(b));
      case 'subEstipulante-desc': return formatAdhesionProposalSubestipulante(b).localeCompare(formatAdhesionProposalSubestipulante(a));
      default: return 0;
    }
  });
};
